import { Injectable } from '@angular/core';
import { CompositeFilterDescriptor, FilterDescriptor, process, State } from '@progress/kendo-data-query';
import { BehaviorSubject, Observable } from 'rxjs';

import { AppGridServiceAbstract } from './appgrid.service.abstract';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { map, tap } from 'rxjs/operators';
import { ApiHttpService, HttpRequestOptions } from 'src/app/services/api-http.service';
import { IAppGridParams, IDashboardFilter } from 'src/app/home/filter/filter.model';
import { HttpParams } from '@angular/common/http';

// This constant defines a mapping of column fields to their corresponding database fields for the 'notification/rule' API.
// It is used to translate front-end column field names to back-end database column names for sorting purposes.

const SORT_COLUMN_MAPPING: Record<string, null | Record<string, string>> = {
    'notification/rule/pagination': {
        'worldWideEnabled': 'r.ww_enabled_yn',
        "area.area": "a.area",
        "country.country": "c.country",
        "localLevel.localLevel1": "r.locallevel1",
        "localLevel.localLevel2": "r.locallevel2",
        "algorithmPLIndicator.algorithmName": "apid.algorithm_name",
        "algorithmEnabled": "algorithm_enabled",
        "algorithmPLIndicator.productLineName": "apid.prod_line_name",
        "algorithmPLIndicator.algIndicatorName": "apid.alg_indicator_name",
        "areaEnabled": "r.area_enabled_yn",
        "countryEnabled": "r.country_enabled_yn",
        "localLevelEnabled": "r.locallevel_enabled_yn",
        "overallEnabled": "r.overall_enabled_yn",
        "tktNtfnEnabled": "r.tkt_ntfn_enabled_yn",
        "tktOwnerType": "r.tkt_owner_type",
        "emailNtfnEnabled": "r.email_ntfn_enabled_yn",
        "emailOthersIds": "r.email_other_ids",
        "oneviewNtfnEnabled": "r.oneview_ntfn_enabled_yn",
        "oneviewOthersIds": "r.oneview_other_ids",
        "algorithmPLIndicator.productFamilyName": "apid.prod_family_name",
        "createdBy": "r.created_by",
        "createdDate": "r.created_date",
        "modifiedBy": "r.modified_by",
        "modifiedDate": "r.modified_date",
        "emailNtfnTemplate.name": "t.ntfn_template_name",
    }
}

const FILTER_COLUMN_MAPPING: Record<string, null | Record<string, string | Record<string, null>>> = {
    'notification/rule/pagination': {
        "area.area": { "area": null },
        "country.country": { "country": null },
        "localLevel.localLevel1": "localLevel1",
        "localLevel.localLevel2": "localLevel2",
        "algorithmPLIndicator.productLineName": "productLineName",
        "algorithmPLIndicator.productFamilyName": "productFamilyName",
        "algorithmPLIndicator.algorithmName": "algorithmName",
        "algorithmPLIndicator.algIndicatorName": "algorithmIndicatorName",
        "tktOwnerType": "tktOwnerType",
        "emailNtfnTemplate.name": "emailNtfnTemplate",
        "emailOthersIds": "emailOthersIds",
        "oneviewOthersIds": "oneviewOthersIds",
        "modifiedBy": "modifiedBy",
        "createdBy": "createdBy",
        "worldWideEnabled": "worldWideEnabled",
        "areaEnabled": "areaEnabled",
        "countryEnabled": "countryEnabled",
        "localLevelEnabled": "localLevelEnabled",
        "algorithmEnabled": "algorithmEnabled",
        "overallEnabled": "overallEnabled",
        "tktNtfnEnabled": "tktNtfnEnabled",
        "emailNtfnEnabled": "emailNtfnEnabled",
        "oneviewNtfnEnabled": "oneviewNtfnEnabled",
        "emailTSSForGrid": "emailTSSForGrid",
        "oneviewTSSForGrid": "oneviewTSSForGrid",
        "modifiedDate": "modifiedDate",
        "createdDate": "createdDate",
        "allowNotification": "allowNotification"
    }
}

@Injectable()
export class AppGridClientService extends AppGridServiceAbstract {
    private static regexIso8601 = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{0,5})$/;
    private static regexIso8601Date = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6})$/;
    // private gridRawData = new Map<string, any[]>();
    public override gridRawData: { [id: string]: any[] } = {};
    public override gridRawDataTotal: { [id: string]: number } = {};

    private static convertDateStringsToDates(input: any[]): any[] {
        // Handles if input is undefined
        if (!input) {
            return [];
        }

        // Handles if input is not an array
        if (!Array.isArray(input)) {
            return input;
        }

        for (let row of input) {
            for (let key in row) {
                if (!row.hasOwnProperty(key)) {
                    continue;
                }

                let value = row[key];
                let match;
                // Check for string properties which look like dates.
                if (typeof value === 'string' && (match = value.match(AppGridClientService.regexIso8601))) {
                    row[key] = new Date(value.substring(0, 16) + 'Z');
                } else if (typeof value === 'string' && (match = value.match(AppGridClientService.regexIso8601Date))) {
                    row[key] = new Date(value.substring(0, 16) + 'Z');
                }
            }
        }

        return input;
    }

    constructor(public override _http: ApiHttpService) {
        super(_http);
    }

    public query(api: string, state: State, forceUpdate: boolean, filter: IDashboardFilter, queryData: boolean, algoId?: number, isDynamicAppGrid = false) {
        // Check if the grid is dynamic
        if (isDynamicAppGrid) {
            if (this.gridData[api] == null) {
                this.gridData[api] = new BehaviorSubject<GridDataResult>({ data: [], total: 0 });
            }

            // Initialize an empty array to hold the sort parameters
            const sort: string[] = [];
            (state.sort ?? []).forEach(obj => {
                if (SORT_COLUMN_MAPPING[api]?.[obj.field] && obj.dir) {
                    sort.push(`${SORT_COLUMN_MAPPING[api][obj.field]},${obj.dir ?? 'desc'}`)
                }
            })

            // Converts a Date object to a string in 'YYYY-MM-DDTHH:MM' format with time zone.
            function formatDateTime(dateString: string): string {
                const date = new Date(dateString);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');

                const timeZoneOffset = date.getTimezoneOffset();
                const sign = timeZoneOffset >= 0 ? '-' : '+';
                const offsetHours = String(Math.floor(Math.abs(timeZoneOffset) / 60)).padStart(2, '0');
                const offsetMinutes = String(Math.abs(timeZoneOffset) % 60).padStart(2, '0');

                return `${year}-${month}-${day}T${hours}:${minutes}${sign}${offsetHours}:${offsetMinutes}`; // Returns 'YYYY-MM-DDTHH:MM±HH:MM'
            }



            // Converts a date string to the required format based on the field ('modifiedDate' or 'createdDate').
            function convertToRequiredFormat(date: string, field: string): string {
                if (field === 'modifiedDate' || field === 'createdDate') {
                    return formatDateTime(date); // Format as 'YYYY-MM-DDTHH:MM'
                }
                return date;
            }

            // Initialize an empty array to hold the filter parameters
            const filters: { [key: string]: any } = {};

            // Constructs a filters object based on the state filter descriptors, mapping fields and formatting dates as needed.
            if (state && state.filter) {
                const filterDescriptors = state.filter as CompositeFilterDescriptor;
                if (filterDescriptors.filters) {
                    filterDescriptors.filters.forEach(obj => {
                        if ('field' in obj && typeof obj.field === 'string') {
                            const mappedField = FILTER_COLUMN_MAPPING[api]?.[obj.field];

                            if (mappedField) {
                                if (typeof mappedField === 'object' && mappedField !== null) {
                                    Object.keys(mappedField).forEach(key => {
                                        if (obj.value !== null && obj.value !== '') {
                                            filters[key] = { [key]: obj.value };
                                        }
                                    });
                                } else if (typeof mappedField === 'string') {
                                    if (mappedField === 'modifiedDate' || mappedField === 'createdDate') {
                                        const formattedDate = convertToRequiredFormat(obj.value, mappedField);
                                        if (formattedDate !== null && formattedDate !== '') {
                                            filters[mappedField] = formattedDate; // Use the required date format in UTC
                                        }
                                    } else {
                                        if (obj.value !== null && obj.value !== '') {
                                            filters[mappedField] = obj.value;
                                        }
                                    }
                                }
                            }
                        }
                    });
                }
            }

            // Calculate the current page number based on the skip and take values
            const page = (state.skip / state.take) + 1;
            const requestBody = { page, size: state.take, sort, ...filters };
            this.loadDynamicGridData<{ results: any[], totalRecords: number }>(api, requestBody).subscribe({
                next: gridData => {
                    if (page === 1) {
                        this.gridRawDataTotal[api] = gridData.totalRecords ?? 0;
                    }
                    this.gridRawData[api] = AppGridClientService.convertDateStringsToDates(gridData.results);
                },
                error: error => this.gridData[api].next(process([], state)),
                complete: () => {
                    // Update the grid data with the new data and total record count
                    this.gridData[api].next({ data: this.gridRawData[api], total: this.gridRawDataTotal[api] ?? 0 });

                    this._valueChanges.emit(this.gridRawData[api]);
                }
            });
            return;
        }


        if (this.gridRawData[api] == null || forceUpdate) {
            if (this.gridData[api] == null) {
                this.gridData[api] = new BehaviorSubject<GridDataResult>({ data: [], total: 0 });
            }

            if (queryData) {
                this.loadGridData(api, filter, algoId).subscribe({
                    next: gridData => {
                        gridData = (api.indexOf('algorithms/alg_job_run_details') === -1) ? gridData : ((gridData as unknown) as { detailsList: any }).detailsList;
                        // Conditionally extract 'detailsList' from gridData based on API path match.
                        this.gridRawData[api] = AppGridClientService.convertDateStringsToDates(gridData);
                    },
                    error: error => this.gridData[api].next(process([], state)),
                    complete: () => {
                        this.gridData[api].next(process(this.gridRawData[api], state));
                        this._valueChanges.emit(this.gridRawData[api]);
                    }
                });
            } else {
                this.gridData[api].next(process(this.gridRawData[api], state));
            }
        } else {
            this.gridData[api].next(process(this.gridRawData[api], state));
        }
    }

    //Loads the data for the given api
    private loadGridData<T = any[]>(api: string, filter: IDashboardFilter, algoId?: number, queryParams?: IAppGridParams): Observable<T> {
        if (filter != null) {
            return this._http.post(api, filter, undefined, false).lift(map((res: Response) => res.json()));
        } else {
            if (algoId) {
                return this._http.get(api + algoId, undefined, queryParams);
            }

            return this._http.get(api, undefined, queryParams);
        }

    }

    //Loads the data for dynamic grid
    private loadDynamicGridData<T = any[]>(api: string, body?: any): Observable<T> {
        const options = this.convertParamsToHttpRequestOptions(body);
        return this._http.post<T>(api, body, options, true);
    }


    // Helper method to convert IAppGridParams to HttpRequestOptions
    private convertParamsToHttpRequestOptions(params?: IAppGridParams): HttpRequestOptions {
        if (!params) {
            return {};
        }
        const httpParams = new HttpParams({ fromObject: params });
        return { params: httpParams } as any; // Type assertion to bypass the type error
    }

    public setData(api: string, data: any[]) {
        this.gridRawData[api] = data;
    }

    //Returns the data for the given api
    public getData(api: string): any[] {
        return this.gridRawData[api];
    }

    //When the grid component is being destroyed we should remove our cached data for that api
    public clearData(api: string): void {
        this.gridRawData[api] = [];
    }

    public deleteRowData(api: string, idField: string, id: any) {
        let index = this.gridRawData[api].findIndex((g: any) => g[idField] === id);
        if (index !== -1) {
            this.gridRawData[api].splice(index, 1);
        }
    }

    // Below method retrieves and processes grid data for Excel export, handling date filters.
    public getExcelData(api: string, state: State): ExcelExportData {
        if (this.gridRawData[api]) {
            let excelState: State = JSON.parse(JSON.stringify(state));
            excelState.skip = 0;
            excelState.take = 1000000;
            // Custom filter logic for date fields
            excelState.filter.filters.forEach(filter => {
                if (this.isFilterDescriptor(filter) && (filter.field === 'modifiedDate' || filter.field === 'createdDate')) {
                    filter.value = new Date(filter.value);
                }
            });

            let gridData = process(this.gridRawData[api], excelState);
            return {
                data: this.gridRawData[api]
            };
        }
        return {};
    }

    // Type guard to check if the filter is a FilterDescriptor
    isFilterDescriptor(filter: CompositeFilterDescriptor | FilterDescriptor): filter is FilterDescriptor {
        return (filter as FilterDescriptor).field !== undefined;
    }

}